/**
 * TruCoin unit conversion — the single source of truth.
 *
 * 1 TruCoin = ₹0.50 = 50 paise (locked; mirrored by
 * `loyalty_program_config.coin_value_paise` in the database with a CHECK).
 *
 * ALL loyalty math must stay in integer paise. Use these helpers instead of
 * ad-hoc divisions anywhere the UI or a formatter needs a TruCoin count or
 * rupee display.
 */

/** Paise per one TruCoin. Locked. */
export const POINT_VALUE_PAISE = 50 as const;

/** Convert whole TruCoins → paise. Throws on non-integer / negative input. */
export function coinToPaise(coins: number): number {
  if (!Number.isFinite(coins) || !Number.isInteger(coins) || coins < 0) {
    throw new Error(`coinToPaise: coins must be a non-negative integer, got ${coins}`);
  }
  return coins * POINT_VALUE_PAISE;
}

/** Convert paise → whole TruCoins (rounded DOWN — never fractional coins). */
export function paiseToWholeCoins(paise: number): number {
  if (!Number.isFinite(paise) || !Number.isInteger(paise)) {
    throw new Error(`paiseToWholeCoins: paise must be an integer, got ${paise}`);
  }
  if (paise <= 0) return 0;
  return Math.floor(paise / POINT_VALUE_PAISE);
}

/** Format a paise amount as a coin count for UI ("1,250"). */
export function formatCoinCount(paise: number): string {
  return paiseToWholeCoins(paise).toLocaleString("en-IN");
}

/** Format paise as rupees ("₹10.50", "₹1,234"). Half-up to 2 decimals. */
export function formatRupeesFromPaise(paise: number): string {
  if (!Number.isFinite(paise)) return "₹0";
  const abs = Math.abs(Math.round(paise));
  const sign = paise < 0 ? "-" : "";
  const rupees = Math.floor(abs / 100);
  const fract = abs % 100;
  const rupeesStr = rupees.toLocaleString("en-IN");
  if (fract === 0) return `${sign}₹${rupeesStr}`;
  return `${sign}₹${rupeesStr}.${fract.toString().padStart(2, "0")}`;
}

/**
 * "20 TruCoins = ₹10 discount" — the canonical dual display used across the
 * cart, account ledger, and admin surfaces.
 */
export function formatCoinsWithRupees(paise: number): string {
  return `${formatCoinCount(paise)} TruCoins = ${formatRupeesFromPaise(paise)}`;
}
